import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Chip,
  Button,
  Step,
  StepLabel,
  Card,
  CardContent,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import VerifiedIcon from "@mui/icons-material/Verified";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleIcon from "@mui/icons-material/People";
import LinkIcon from "@mui/icons-material/Link";
import StarIcon from "@mui/icons-material/Star";
import QrCodeIcon from "@mui/icons-material/QrCode";
import FeatureLayout from "./FeatureLayout";

// App screenshot placeholders
import bringCleanerScreenshot1 from "../../../images/features/bring-cleaner-1.png";
import bringCleanerScreenshot2 from "../../../images/features/bring-cleaner-2.png";
import bringCleanerScreenshot3 from "../../../images/features/bring-cleaner-3.png";

const BringYourCleaner = () => {
  const theme = useTheme();

  const benefits = [
    {
      icon: <VerifiedIcon />,
      title: "Keep Your Trusted Team",
      description: "Don't break existing relationships. Bring your current cleaners with you.",
    },
    {
      icon: <EmailIcon />,
      title: "Invite via Email",
      description: "Send personalized invites to your cleaners. They'll love the automation.",
    },
    {
      icon: <SmsIcon />,
      title: "Text Message Invites",
      description: "Invite cleaners directly via SMS. No app download required to start.",
    },
    {
      icon: <QrCodeIcon />,
      title: "QR Code Signup",
      description: "Print QR codes for easy onboarding. Cleaners scan and join instantly.",
    },
    {
      icon: <GroupAddIcon />,
      title: "Bulk Import",
      description: "Import multiple cleaners at once via CSV. Perfect for property managers.",
    },
    {
      icon: <LinkIcon />,
      title: "Shareable Link",
      description: "Create a custom signup link for your cleaners. Track who's joined.",
    },
  ];

  const stats = [
    { value: "30s", label: "To invite a cleaner" },
    { value: "100%", label: "Keep your rates" },
    { value: "No fees", label: "To add cleaners" },
    { value: "Unlimited", label: "Cleaners per property" },
  ];

  const steps = [
    { title: "Generate Invite", description: "Create a unique invite link or QR code" },
    { title: "Send Invitation", description: "Share via email, text, or print QR code" },
    { title: "Cleaner Onboards", description: "They sign up and set their availability" },
    { title: "Start Booking", description: "Assign jobs instantly. They confirm in app" },
  ];

  return (
    <FeatureLayout
      title="Bring Your Cleaner"
      subtitle="Keep your trusted team. Upgrade everything else."
      description="Already have a cleaner you love? Great! Invite them to join FreshSweeper in seconds. They keep their rates, you keep your relationship. Everyone wins with automation and backup coverage."
      icon={<PersonAddIcon sx={{ fontSize: 32 }} />}
      iconGradient="linear-gradient(135deg, #f093fb, #f5576c)"
      breadcrumbs={[
        { label: "Features", link: "/features" },
        { label: "Bring Your Cleaner" },
      ]}
      benefits={benefits}
      stats={stats}
      imageRight={bringCleanerScreenshot1}
      imageLeft={bringCleanerScreenshot2}
    >
      {/* How It Works Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Invite Your Cleaner in 3 Simple Steps
        </Typography>
        
        <Stepper
          activeStep={-1}
          alternativeLabel
          sx={{
            mb: 6,
            flexWrap: { xs: "wrap", md: "nowrap" },
            "& .MuiStepConnector-line": {
              borderColor: alpha(theme.palette.primary.main, 0.3),
            },
          }}
        >
          {steps.map((step, idx) => (
            <Step key={idx} completed={false} active={false}>
              <StepLabel>
                <Typography variant="h6" fontWeight="700">
                  {step.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Container>

      {/* Invite Methods Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Multiple Ways to Invite
        </Typography>
        
        <Grid container spacing={4}>
          {[
            { icon: <EmailIcon />, title: "Email Invite", desc: "Send a professional email with signup link", time: "30 seconds" },
            { icon: <SmsIcon />, title: "Text Message", desc: "Quick SMS invite for mobile-first cleaners", time: "15 seconds" },
            { icon: <QrCodeIcon />, title: "QR Code", desc: "Print and post in your properties", time: "Instant scan" },
            { icon: <LinkIcon />, title: "Shareable Link", desc: "Custom link for your cleaning team", time: "Copy & paste" },
          ].map((method, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "transform 0.2s",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "20px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                    color: theme.palette.primary.main,
                  }}
                >
                  {React.cloneElement(method.icon, { sx: { fontSize: 32 } })}
                </Box>
                <Typography variant="h6" fontWeight="800" gutterBottom>
                  {method.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {method.desc}
                </Typography>
                <Chip
                  label={method.time}
                  size="small"
                  sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* What Cleaners Get Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              What Your Cleaner Gets
            </Typography>
            <List>
              {[
                "Free mobile app for iOS and Android",
                "Automatic job notifications",
                "Easy check-in/check-out with photos",
                "On-time payment protection",
                "Access to backup jobs when available",
                "Ratings and reviews to build reputation",
              ].map((text, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={bringCleanerScreenshot3}
              alt="Cleaner app interface"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Testimonial */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            textAlign: "center",
          }}
        >
          <Typography variant="h1" sx={{ fontSize: "3rem", mb: 2 }}>💬</Typography>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            "I was worried about switching platforms because I love my cleaner."
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary", maxWidth: "600px", mx: "auto" }}>
            "FreshSweeper let me invite her directly. She kept her rate, I kept my peace of mind. 
            Now we both have automation and backup coverage. Best decision ever."
          </Typography>
          <Typography variant="subtitle2" fontWeight="700">
            — Sarah Johnson, Superhost (12 properties)
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mt: 2 }}>
            {[...Array(5)].map((_, i) => (
              <StarIcon key={i} sx={{ fontSize: 20, color: theme.palette.warning.main }} />
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Bulk Import Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6} alignItems="center" direction={{ xs: "column-reverse", md: "row" }}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://placehold.co/600x400/667eea/white?text=CSV+Import+Demo"
              alt="Bulk import interface"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Manage Multiple Cleaners?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              Property managers can import entire cleaning teams at once. Perfect for agencies managing multiple properties.
            </Typography>
            <List>
              {[
                "Bulk import via CSV file",
                "Set default rates per cleaner",
                "Assign preferred properties",
                "Track performance across your team",
              ].map((text, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            textAlign: "center",
            color: "white",
          }}
        >
          <Typography variant="h4" fontWeight="800" gutterBottom>
            Ready to Bring Your Cleaner?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, maxWidth: "500px", mx: "auto" }}>
            Invite them today. They'll thank you for making their job easier.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PersonAddIcon />}
            sx={{
              bgcolor: "white",
              color: theme.palette.primary.main,
              borderRadius: "50px",
              px: 5,
              py: 1.5,
              fontWeight: 700,
              "&:hover": {
                bgcolor: alpha(theme.palette.common.white, 0.9),
                transform: "translateY(-2px)",
              },
            }}
          >
            Invite Your Cleaner Now
          </Button>
        </Paper>
      </Container>
    </FeatureLayout>
  );
};

export default BringYourCleaner;